// AWR 리포트 › 스냅샷 구간 막대 (2026-10-14)
//
// 수평 막대의 양 끝 = 고른 기간의 첫·마지막 스냅샷, 그 위의 작은 세로 막대 둘 = 시작·끝 스냅샷.
// 세로 막대를 끌거나(마우스·터치) 화살표 키로 한 스냅샷씩 옮긴다. 막대의 빈 곳을 누르면 가까운 쪽 손잡이가 그리로 간다.
// 위치는 시각이 아니라 **스냅샷 순번**이다 — 스냅샷 간격이 고르지 않거나(수동 스냅샷) 빈 구간이 있어도 하나씩 고를 수 있어야 한다.
// 인스턴스가 재기동된 자리는 빨간 눈금으로 보인다(그 앞뒤를 한 구간으로 고르면 오라클 리포트도 거절한다).
//
// 좌표: 마우스 위치와 막대의 rect 는 둘 다 화면 px 이라 비율로만 쓴다(앱 전체 확대(CSS zoom) 아래에서도 맞다).
import React, { useMemo, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import './SnapRangeBar.css';

const short = (iso) => (iso ? String(iso).replace('T', ' ').slice(5, 16) : '');

export default function SnapRangeBar({ snaps, beginSnap, endSnap, onChange, disabled }) {
    const { t } = useTranslation();
    const trackRef = useRef(null);
    const dragRef = useRef(null);
    // 받는 목록은 최신부터(DESC) — 막대는 왼쪽이 오래된 것
    const asc = useMemo(() => [...(snaps || [])].sort((a, b) => a.snapId - b.snapId), [snaps]);
    const n = asc.length;
    const idxOf = (id) => asc.findIndex(s => String(s.snapId) === String(id));
    const bi = idxOf(beginSnap);
    const ei = idxOf(endSnap);
    const pos = (i) => (n <= 1 ? 0 : (i / (n - 1)) * 100);
    const restarts = useMemo(
        () => asc.map((s, i) => (i > 0 && s.startup && asc[i - 1].startup && s.startup !== asc[i - 1].startup ? i : -1)).filter(i => i > 0),
        [asc],
    );

    const indexAt = useCallback((clientX) => {
        const el = trackRef.current;
        if (!el || n === 0) return -1;
        const r = el.getBoundingClientRect();
        const f = r.width > 0 ? Math.min(1, Math.max(0, (clientX - r.left) / r.width)) : 0;
        return Math.round(f * (n - 1));
    }, [n]);

    const move = useCallback((which, i) => {
        if (n < 2 || i < 0) return;
        if (which === 'begin') {
            const ni = Math.min(Math.max(0, i), (ei >= 0 ? ei : n - 1) - 1);
            if (ni >= 0 && ni !== bi) onChange(String(asc[ni].snapId), endSnap);
        } else {
            const ni = Math.max(Math.min(n - 1, i), (bi >= 0 ? bi : 0) + 1);
            if (ni < n && ni !== ei) onChange(beginSnap, String(asc[ni].snapId));
        }
    }, [n, bi, ei, asc, beginSnap, endSnap, onChange]);

    const onHandleDown = (which) => (e) => {
        if (disabled) return;
        e.preventDefault();
        e.stopPropagation();
        dragRef.current = which;
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_e) { /* 캡처를 못 하면 막대 위에서만 끌린다 */ }
    };
    const onHandleMove = (e) => {
        if (!dragRef.current) return;
        move(dragRef.current, indexAt(e.clientX));
    };
    const onHandleUp = () => { dragRef.current = null; };
    const onTrackDown = (e) => {
        if (disabled || n < 2) return;
        const i = indexAt(e.clientX);
        // 가까운 손잡이가 온다
        const which = bi < 0 || ei < 0 ? 'end' : (Math.abs(i - bi) <= Math.abs(i - ei) ? 'begin' : 'end');
        move(which, i);
    };
    const onKey = (which) => (e) => {
        const cur = which === 'begin' ? bi : ei;
        const step = { ArrowLeft: -1, ArrowDown: -1, ArrowRight: 1, ArrowUp: 1, PageDown: -10, PageUp: 10 }[e.key];
        if (step != null) { e.preventDefault(); move(which, cur + step); return; }
        if (e.key === 'Home') { e.preventDefault(); move(which, 0); }
        if (e.key === 'End') { e.preventDefault(); move(which, n - 1); }
    };

    if (n < 2) return null;
    const b = bi >= 0 ? asc[bi] : null;
    const en = ei >= 0 ? asc[ei] : null;
    const label = (s) => (s ? `${s.snapId} · ${short(s.endTime)}` : '-');
    return (
        <div className={`snaprange${disabled ? ' is-disabled' : ''}`}>
            <span className="snaprange-edge">{short(asc[0].endTime)}</span>
            <div className="snaprange-body">
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- 손잡이가 키보드 대상이다. 막대 누르기는 마우스 지름길 */}
                <div className="snaprange-track" ref={trackRef} onPointerDown={onTrackDown}>
                    {n <= 240 && asc.map((s, i) => (
                        <span key={s.snapId} className={`snaprange-tick${s.flushError ? ' is-err' : ''}`} style={{ left: `${pos(i)}%` }} />
                    ))}
                    {restarts.map(i => (
                        <span key={`r${i}`} className="snaprange-restart" style={{ left: `${pos(i - 0.5)}%` }}
                            title={t('monitoring:awr.range.restartAt', { snap: asc[i].snapId })} />
                    ))}
                    {bi >= 0 && ei >= 0 && (
                        <span className="snaprange-sel" style={{ left: `${pos(bi)}%`, width: `${pos(ei) - pos(bi)}%` }} />
                    )}
                    {['begin', 'end'].map(which => {
                        const i = which === 'begin' ? bi : ei;
                        if (i < 0) return null;
                        const s = asc[i];
                        return (
                            <span
                                key={which}
                                role="slider"
                                tabIndex={disabled ? -1 : 0}
                                aria-label={t(`monitoring:awr.range.${which}`)}
                                aria-valuemin={asc[0].snapId}
                                aria-valuemax={asc[n - 1].snapId}
                                aria-valuenow={s.snapId}
                                aria-valuetext={label(s)}
                                className={`snaprange-handle is-${which}`}
                                style={{ left: `${pos(i)}%` }}
                                title={`${t(`monitoring:awr.range.${which}`)} · ${label(s)}`}
                                onPointerDown={onHandleDown(which)}
                                onPointerMove={onHandleMove}
                                onPointerUp={onHandleUp}
                                onPointerCancel={onHandleUp}
                                onKeyDown={onKey(which)}
                            />
                        );
                    })}
                </div>
                <div className="snaprange-labels">
                    <span>{t('monitoring:awr.range.begin')} <b>{label(b)}</b></span>
                    <span className="snaprange-count">{bi >= 0 && ei >= 0 ? t('monitoring:awr.range.count', { n: ei - bi }) : ''}</span>
                    <span>{t('monitoring:awr.range.end')} <b>{label(en)}</b></span>
                </div>
            </div>
            <span className="snaprange-edge">{short(asc[n - 1].endTime)}</span>
        </div>
    );
}

SnapRangeBar.propTypes = {
    snaps: PropTypes.array,
    beginSnap: PropTypes.string,
    endSnap: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};
